'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { signOut } from '../../../infrastructure/session/client';
import { notifySessionExpired } from '../../../infrastructure/session-management/sessionExpiryCoordinator';
import { getErrorDiagnostics, getSafeErrorDiagnostics, getSentryErrorContext, getUnauthorizedReason, getUserSafeErrorMessage, HttpError, isChunkLoadError, isSessionExpiredError, logErrorDiagnosticsToConsole, reloadForChunkError, reportError, UNAUTHORIZED_REASON_COPY } from '../../../utilities/errors';
import { PageContainer } from '../../layouts';
import { Layout } from '../../material';
import { UserFriendlyError } from '../UserFriendlyError';
export type DiagnosticErrorFallbackProps = {
  error: Error;
  reset?: () => void;
  onGoHome?: () => void;
  /** Label used in Sentry messages and console logs (e.g. ErrorPage, ErrorBoundary). */
  source?: string;
  /** Full-page layout wraps content in PageContainer; embedded fits inside existing layouts. */
  layout?: 'page' | 'embedded';
  /** React component stack from the error boundary, when available. */
  componentStack?: string | null;
  /** When true, session-expiry errors trigger the graceful sign-out flow. */
  handleSessionExpiry?: boolean;
  /** Optional override for the error title (non-session errors only). */
  title?: string;
  /** Optional override for the user-facing message (non-session errors only). */
  message?: string;
};
function normalizeError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }
  return new Error(String(error ?? 'Unknown error'));
}

/**
 * Unified error-boundary UI with support diagnostics, Sentry correlation, and
 * user-friendly messaging. Used by Next.js error pages and react-error-boundary fallbacks.
 */
export function DiagnosticErrorFallback(t0) {
  const $ = _c(44);
  const {
    error: rawError,
    reset,
    onGoHome,
    source: t1,
    layout: t2,
    componentStack,
    handleSessionExpiry: t3,
    title: t4,
    message: messageOverride
  } = t0;
  const source = t1 === undefined ? "ErrorBoundary" : t1;
  const layout = t2 === undefined ? "embedded" : t2;
  const handleSessionExpiry = t3 === undefined ? false : t3;
  const title = t4 === undefined ? "Something went wrong" : t4;
  const error = normalizeError(rawError);
  const [errorId, setErrorId] = React.useState(null);
  const [isRecovering, setIsRecovering] = React.useState(false);
  const hasReportedRef = React.useRef(false);
  const isSessionExpiry = handleSessionExpiry && isSessionExpiredError(error);
  const unauthorizedReason = getUnauthorizedReason(error);
  let t5;
  if ($[0] !== error) {
    t5 = getErrorDiagnostics(error);
    $[0] = error;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const diagnostics = t5;
  let t6;
  if ($[2] !== diagnostics) {
    t6 = getSafeErrorDiagnostics(diagnostics);
    $[2] = diagnostics;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const publicDiagnostics = t6;
  let t7;
  if ($[4] !== componentStack || $[5] !== diagnostics || $[6] !== error || $[7] !== setErrorId || $[8] !== source) {
    t7 = () => {
      if (hasReportedRef.current) {
        return;
      }
      hasReportedRef.current = true;
      const requestId = error instanceof HttpError ? error.requestId : diagnostics.requestId;
      const sentryContext = getSentryErrorContext(error);
      const {
        id
      } = reportError(error, `Error caught by ${source}`, {
        tags: {
          ...(requestId ? {
            requestId
          } : {}),
          errorName: diagnostics.errorName,
          errorSource: source,
          ...(diagnostics.failureReason ? {
            failureReason: diagnostics.failureReason
          } : {}),
          ...(diagnostics.queryName ? {
            queryName: diagnostics.queryName
          } : {})
        },
        extra: sentryContext,
        ...(componentStack ? {
          contexts: {
            react: {
              componentStack
            }
          }
        } : {})
      });
      setErrorId(requestId ?? id);
      logErrorDiagnosticsToConsole(`[${source}]`, error, diagnostics);
    };
    $[4] = componentStack;
    $[5] = diagnostics;
    $[6] = error;
    $[7] = setErrorId;
    $[8] = source;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== componentStack || $[11] !== diagnostics || $[12] !== error || $[13] !== source) {
    t8 = [error, diagnostics, source, componentStack];
    $[10] = componentStack;
    $[11] = diagnostics;
    $[12] = error;
    $[13] = source;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  React.useEffect(t7, t8);
  let t10;
  let t9;
  if ($[15] !== isSessionExpiry || $[16] !== unauthorizedReason) {
    t9 = () => {
      if (isSessionExpiry) {
        notifySessionExpired(unauthorizedReason ?? "unknown");
      }
    };
    t10 = [isSessionExpiry, unauthorizedReason];
    $[15] = isSessionExpiry;
    $[16] = unauthorizedReason;
    $[17] = t10;
    $[18] = t9;
  } else {
    t10 = $[17];
    t9 = $[18];
  }
  React.useEffect(t9, t10);
  let t11;
  if ($[19] !== onGoHome) {
    t11 = () => {
      if (onGoHome) {
        onGoHome();
        return;
      }
      if (typeof globalThis.location !== "undefined") {
        globalThis.location.href = "/";
      }
    };
    $[19] = onGoHome;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const handleGoHome = t11;
  const handleSignInAgain = _temp;
  let t12;
  if ($[21] !== error || $[22] !== reset || $[23] !== setIsRecovering) {
    t12 = () => {
      if (isChunkLoadError(error) && reloadForChunkError()) {
        setIsRecovering(true);
        return;
      }
      reset?.();
    };
    $[21] = error;
    $[22] = reset;
    $[23] = setIsRecovering;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const handleRetry = t12;
  let t13;
  if ($[25] !== componentStack || $[26] !== error || $[27] !== errorId || $[28] !== handleGoHome || $[29] !== handleRetry || $[30] !== isRecovering || $[31] !== isSessionExpiry || $[32] !== messageOverride || $[33] !== publicDiagnostics || $[34] !== title || $[35] !== unauthorizedReason) {
    t13 = isSessionExpiry ? <UserFriendlyError title="Your session has ended" alertTitle="What happened" message={UNAUTHORIZED_REASON_COPY[unauthorizedReason ?? "unknown"].message} details={UNAUTHORIZED_REASON_COPY[unauthorizedReason ?? "unknown"].details} severity="warning" errorId={errorId || undefined} retryLabel="Sign in again" onRetry={handleSignInAgain} /> : <UserFriendlyError title={title} message={messageOverride ?? getUserSafeErrorMessage(error)} errorId={errorId || undefined} onRetry={handleRetry} retryPending={isRecovering} onGoHome={handleGoHome} diagnosticInfo={{
      diagnostics: publicDiagnostics,
      componentStack,
      errorStack: error.stack
    }} />;
    $[25] = componentStack;
    $[26] = error;
    $[27] = errorId;
    $[28] = handleGoHome;
    $[29] = handleRetry;
    $[30] = isRecovering;
    $[31] = isSessionExpiry;
    $[32] = messageOverride;
    $[33] = publicDiagnostics;
    $[34] = title;
    $[35] = unauthorizedReason;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  const content = t13;
  const t14 = layout === "page" ? 4 : 2;
  let t15;
  if ($[37] !== t14) {
    t15 = {
      py: t14,
      maxWidth: 640,
      width: "100%"
    };
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== content || $[40] !== t15) {
    t16 = <Layout.BoxCenter><Layout.BoxVertical sx={t15}>{content}</Layout.BoxVertical></Layout.BoxCenter>;
    $[39] = content;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  const inner = t16;
  if (layout === "page") {
    let t17;
    if ($[42] !== inner) {
      t17 = <PageContainer>{inner}</PageContainer>;
      $[42] = inner;
      $[43] = t17;
    } else {
      t17 = $[43];
    }
    return t17;
  }
  return inner;
}
function _temp() {
  signOut({
    redirectTo: typeof window !== "undefined" ? window.location.origin : "/"
  });
}